/**
 * "Docs" — the items lens: the knowledge tree as folders, like the repo.
 *
 * domain → subcategory FOLDER → item list → item workspace (read, answer the
 * questions in place, run). Folders are the subcategory facet ("General" for
 * none); every row shows counts so nothing is a mystery.
 */

import { useState } from "react";
import type { Item } from "../lib/item";
import type { Question } from "../lib/questions";
import { StateChip } from "./Chips";

export function Docs({
  items,
  questions,
  onOpenItem,
}: {
  items: Item[];
  questions: Question[];
  onOpenItem: (item: Item) => void;
}) {
  const [domain, setDomain] = useState<string | null>(null);
  const [folder, setFolder] = useState<string | null>(null);

  const openFor = (item: Item) =>
    questions.filter((q) => q.links.includes(item.path) && q.status === "open").length;
  const domains = [...new Set(items.map((i) => i.domain))].sort();
  const folderName = (item: Item) => item.subcategory || "General";

  if (items.length === 0) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 pb-32 pt-4">
        <h1 className="mb-3 text-xl font-semibold text-ink-100">Docs</h1>
        <div className="card p-6 text-center text-sm text-ink-400">
          No items yet — refresh the data.
        </div>
      </div>
    );
  }

  // Level 3: the items inside one folder.
  if (domain !== null && folder !== null) {
    const list = items
      .filter((i) => i.domain === domain && folderName(i) === folder)
      .sort((a, b) => a.title.localeCompare(b.title));
    return (
      <div className="mx-auto w-full max-w-2xl px-4 pb-32 pt-4">
        <button
          onClick={() => setFolder(null)}
          className="mb-3 text-sm text-ink-400 hover:text-ink-200"
        >
          ‹ {domain}
        </button>
        <h1 className="mb-3 text-xl font-semibold text-ink-100">{folder}</h1>
        <div className="space-y-1.5">
          {list.map((item) => {
            const open = openFor(item);
            return (
              <button
                key={item.path}
                onClick={() => onOpenItem(item)}
                className="card flex w-full items-start gap-2 p-3 text-left hover:bg-ink-850"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink-200">
                    {item.title}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-ink-500">
                    <StateChip state={item.state} />
                    {item.created && <span>{item.created}</span>}
                    {open > 0 && (
                      <span className="chip bg-accent/15 text-accent-soft">{open} open</span>
                    )}
                  </span>
                </span>
                <span className="shrink-0 text-ink-500">›</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Level 2: the folders of one domain.
  if (domain !== null) {
    const inDomain = items.filter((i) => i.domain === domain);
    const folders = new Map<string, Item[]>();
    for (const item of inDomain) {
      const key = folderName(item);
      folders.set(key, [...(folders.get(key) ?? []), item]);
    }
    const names = [...folders.keys()].sort((a, b) => {
      if (a === "General") return -1;
      if (b === "General") return 1;
      return a.localeCompare(b);
    });
    return (
      <div className="mx-auto w-full max-w-2xl px-4 pb-32 pt-4">
        <button
          onClick={() => setDomain(null)}
          className="mb-3 text-sm text-ink-400 hover:text-ink-200"
        >
          ‹ All domains
        </button>
        <h1 className="mb-3 text-xl font-semibold capitalize text-ink-100">{domain}</h1>
        <div className="space-y-1.5">
          {names.map((name) => {
            const list = folders.get(name) ?? [];
            const openCount = list.reduce((n, i) => n + openFor(i), 0);
            return (
              <button
                key={name}
                onClick={() => setFolder(name)}
                className="card flex w-full items-center justify-between gap-2 p-3 text-left hover:bg-ink-850"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span aria-hidden>📁</span>
                  <span className="truncate text-sm font-medium text-ink-200">{name}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {openCount > 0 && (
                    <span className="chip bg-accent/15 text-accent-soft">{openCount} open</span>
                  )}
                  <span className="chip bg-ink-850 text-ink-500">
                    {list.length} item{list.length === 1 ? "" : "s"}
                  </span>
                  <span className="text-ink-500">›</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Level 1: domains.
  return (
    <div className="mx-auto w-full max-w-2xl px-4 pb-32 pt-4">
      <h1 className="mb-3 text-xl font-semibold text-ink-100">Docs</h1>
      <div className="space-y-2">
        {domains.map((d) => {
          const inDomain = items.filter((i) => i.domain === d);
          const openCount = inDomain.reduce((n, i) => n + openFor(i), 0);
          return (
            <button
              key={d}
              className="card flex w-full items-center justify-between p-4 text-left hover:bg-ink-850"
              onClick={() => {
                setDomain(d);
                setFolder(null);
              }}
            >
              <span className="text-[15px] font-medium capitalize text-ink-100">{d}</span>
              <span className="flex items-center gap-1.5">
                {openCount > 0 && (
                  <span className="chip bg-accent/15 text-accent-soft">{openCount} open</span>
                )}
                <span className="chip bg-ink-850 text-ink-500">
                  {inDomain.length} item{inDomain.length === 1 ? "" : "s"}
                </span>
                <span className="text-ink-500">›</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
